import * as React from "react";
import {
  getInvoice,
  toggleReoccurringInvoice,
  cancelInvoice,
  refundInvoice,
} from "../../../backend-services";
import { IInvoice } from "../../../@types";
import PageLoader from "../../../components/common/PageLoader";
import { useNavigate, useParams } from "react-router-dom";
import { Annotation } from "../../../@types/annotation";
import { ViewInvoice } from "../../../components/Sales/invoice/view/ViewInvoice";
import { showToast } from "../../../utils/toastUtils";

const ViewInvoicePage: React.FC = () => {
  const { invoiceNo } = useParams();
  if (!invoiceNo) {
    throw new Error("Invoice Id is missing");
  }
  const [invoice, setInvoice] = React.useState<IInvoice>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isRefunding, setIsRefunding] = React.useState(false);
  const [areDatesValid, setAreDatesValid] = React.useState<boolean>(true);
  const [isChecked, setIsChecked] = React.useState(false);
  const [previousChecked, setPreviousChecked] = React.useState(false);
  const [annotations, setAnnotations] = React.useState<Annotation[]>([]);
  const navigate = useNavigate();

  const handleToggleReoccurringInvoice = React.useCallback(
    async ({
      isReoccurringEndDate,
      reoccurringGroupName,
    }: {
      isReoccurringEndDate: string;
      reoccurringGroupName: string;
    }) => {
      if (!areDatesValid) {
        setIsLoading(false);
        setIsChecked(false);
        setPreviousChecked(false);
        return;
      }
      try {
        const invoice = await toggleReoccurringInvoice({
          id: invoiceNo,
          isReoccurringEndDate: isReoccurringEndDate
            ? new Date(isReoccurringEndDate).toISOString()
            : undefined,
          reoccurringGroupName,
        });
        setInvoice(invoice.invoice);
        showToast("Success! Your invoice is now set to reoccur.", "success")
      } catch (e) {
        setIsLoading(false);
        setIsChecked(false);
        setPreviousChecked(false);
      }
    },
    [invoiceNo, areDatesValid],
  );

  const handleCancelInvoice = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const invoice = await cancelInvoice({ id: invoiceNo });
      setIsLoading(false);
      setInvoice(invoice);
    } catch (e) {
      setIsLoading(false);
      showToast("Failed to cancel invoice", "error")
    }
  }, [invoiceNo]);

  const handleRefundInvoice = React.useCallback(async () => {
    setIsRefunding(true);
    try {
      await refundInvoice({ id: invoiceNo });
      setIsRefunding(false);
      fetchInvoice();
      showToast("Invoice refunded successfully!", "success")
    } catch (e) {
      showToast("Failed to refund invoice.", "error")
    } finally {
      setIsRefunding(false);
    }
  }, [invoiceNo]);

  const fetchInvoice = React.useCallback(async () => {
    try {
      const data = await getInvoice(invoiceNo);
      setInvoice(data);
      const annotationsData =
        data?.metadata?.annotations || data?.annotations || [];
      setAnnotations(Array.isArray(annotationsData) ? annotationsData : []);
      setIsLoading(false);
    } catch (error) {
      showToast("Failed to load invoice", "error")
      setIsLoading(false);
    }
  }, [invoiceNo]);

  React.useEffect(() => {
    fetchInvoice();
    const intervalId = setInterval(() => {
      fetchInvoice();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [fetchInvoice]);

  return (
    <div className="">
      {isLoading ? (
        <PageLoader></PageLoader>
      ) : (
        <ViewInvoice
          invoice={invoice}
          annotations={annotations}
          isRefunding={isRefunding}
          handleReOccurringInvoice={handleToggleReoccurringInvoice}
          handleCancelInvoice={handleCancelInvoice}
          handleRefundInvoice={handleRefundInvoice}
          customerId={""}
        />
      )}
    </div>
  );
};
export { ViewInvoicePage };
